import { useAsyncExecutor2 } from "async-executor-hook";
import { differenceInDays, format, parse } from "date-fns";
import { Checkbox } from "primereact/checkbox";
import queryString from "query-string";
import { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";

import {
  combineLatest,
  filter,
  from,
  interval,
  map,
  mapTo,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
  zip,
} from "rxjs";
import { DivCheckbox } from "../../../common/components/PaymentQuotedTableStyles";
import { HTTP_METHODS } from "../../../utils/constants";
import { networkBackendRequest } from "../../../utils/networkUtils";
import { EditButton } from "../../IconButtons";
import { TableHeader } from "../../Invoices/InvoicesStyles";
import PaymentLoader from "../../PaymentLoader/PaymentLoader";
import {
  ColoredLabel,
  ObservationDiv,
  VolanteRow,
} from "../../VctVolante/VctVolanteStyles";
import { formatNumber } from "../../../utils/numbers";
import { dateToRegularFormat } from "../../../utils/dates";
import { extractVolanteStatus } from "../../Payment/common";
import { StatusSectionDetails } from "../../Payment/common/PaymentTableStatus";
import VctVolanteEdition from "../../VctVolanteEdition/VctVolanteEdition";
import { Authorization } from "../../../common/AuthorizationCpt";
import { Paginator } from "primereact/paginator";
import VctAppointmentDiffDaysModal from "../../VctAppointmentDiffDaysModal/VctAppointmentDiffDaysModal";
import VctAppointmentRequest from "../../VctAppointmentRequest/VctAppointmentRequest";
import {
  Container,
  ButtonContainer,
  MainButton,
} from "../../Payment/PaymentStyles";
import loadingGreen from "../../../images/loading_gray.gif";
import { VctVolantesContainer } from "../../VctVolantes/VctVolantesStyles";
import StandaloneMessage from "../../StandaloneMessage/StandaloneMessage";
import ConfirmationModal from "../../ConfirmationModal/ConfirmationModal";
import { useHistory } from "react-router-dom";
import { ConfirmationModalThird } from "../../ConfirmationModal/ConfirmationModalThird";
import { getErrorValueResponse } from "../../../common/error/Error";
import { USER_ROUTES } from "../../../utils/routeConstants";
import {
  CreateAppointment,
  CreateAppointmentVolante,
  ITurnCreationParamsTable,
  Volante,
} from "../TurnCreation.interface";

const POLLING_INTERVAL = 10000;
const ITEMS_PER_PAGE = 8;

const RESULT = {
  DIFFERENT_DAYS: "DIFFERENT_DAYS",
  OK: "OK",
};

const PENDING_PAYMENT = "pending_payment";

const TITLES = {
  select: "SELECCIONAR",
  importer: "IMPORTADOR",
  weight: "PESO",
  groups: "GRUPOS",
  date: "FECHA DE RETIRO",
  status: "ESTADO",
  observation: "OBSERVACIÓN",
  actions: "ACCIONES",
};
function isWithdrawalExpiredDate(withdrawalDate) {
  const LIMIT_DAYS = 5;
  const myvalidateDate: any = parse(withdrawalDate, "yyyy-MM-dd", new Date());
  const datenow = new Date();
  const result = differenceInDays(myvalidateDate, datenow);
  if (result < 0 || result >= LIMIT_DAYS - 1) {
    return true;
  } else {
    return false;
  }
}

export const convertAppointmentResponse = (resp: CreateAppointment) => {
  const volantes = resp.volantes.map((detail: CreateAppointmentVolante) => {
    const status = extractVolanteStatus(detail);
    const hasStatusToDisable =
      detail.additional_charges_status.option === PENDING_PAYMENT ||
      detail.sitradi_block_status.option === "blocked" ||
      detail.sitradi_immobilization_status.option === "immobilized" ||
      isWithdrawalExpiredDate(detail.withdrawal_date);
    const newDate = new Date();
    const dateToday = format(newDate, "yyyy-MM-dd");
    const disabledCheckbox =
      status.problems.length > 0 ||
      detail.payment_status.option === "rejected" ||
      !(detail.withdrawal_date >= dateToday);
    return {
      hasStatusToDisable,
      status,
      disabledCheckbox,
      disabledPending: !disabledCheckbox,
      id: detail.volante_num,
      partNum: detail.part_num,
      masterWaybill: detail.master_air_waybill,
      houseWaybill: detail.house_air_waybill,
      sitradiBlockStatus: detail.sitradi_block_status,
      sitradiImmobilizationStatus: detail.sitradi_immobilization_status,
      aceBlockStatus: detail.dam["ace_block_status"],
      paymentStatus: detail.payment_status,
      additionalChargesStatus: detail.additional_charges_status,
      consigneeName: detail.consignee_name,
      consigneeRuc: detail.consignee_ruc,
      airline: detail.airline,
      groupsNum: detail.groups_num,
      parcelsWeight: detail.parcels_weight,
      parcelsNum: detail.parcels_num,
      withdrawalDate: detail.withdrawal_date,
      paymentDate: detail.payment_date,
      handlingPaymentStatus: detail.handling_payment_status,
    };
  });

  const disabledAll = volantes.every((item) => item.disabledCheckbox);

  return {
    volantes,
    disabledAll,
    num_pages: resp.num_pages,
    page_size: resp.page_size,
    count: resp.count,
  };
};

export const TurnCreationTable = (props: ITurnCreationParamsTable) => {
  const { params$, shoppingCar } = props;

  const destroy$Ref = useRef(new Subject());
  const destroy$: any = destroy$Ref.current;

  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);
  const [dateUrl, setDateUrl] = useState(new Date());
  const loadInvoices = useAsyncExecutor2(
    (params) => {
      const destroy$ = destroy$Ref.current;
      return interval(POLLING_INTERVAL).pipe(
        takeUntil(destroy$),
        startWith(params),
        mapTo(params),
        switchMap((params: any) => {
          setDateUrl(params.from_date);
          const request = networkBackendRequest(
            "/volantes/appointment/",
            HTTP_METHODS.GET,
            params,
            undefined,
            undefined
          )
            .then((resp) => resp.json())
            .then(convertAppointmentResponse);
          return from(request);
        })
      );
    },
    {
      params$: params$.pipe(
        takeUntil(destroy$Ref.current),
        map((params) => ({
          ...params,
          from_date: format(params.from_date, "yyyy-MM-dd"),
          to_date: format(params.to_date, "yyyy-MM-dd"),
          order: "descending",
          page: params.page ?? 1,
          page_size: ITEMS_PER_PAGE,
          has_appointment: false,
          payment_status_option: "paid",
        }))
      ),
    }
  );

  const [invoicesStatus, setInvoicesStatus] = useState<any>({
    status: "",
    data: { volantes: [], num_page: 0, page_size: 0, params: {} },
  });

  useEffect(() => {
    const checkedInvoices$ = combineLatest(
      loadInvoices.state$,
      shoppingCar.items.valueChanges.pipe(
        startWith(shoppingCar.items.value),
        tap(() => {})
      ),
      (volantesStatus, addedItems: any) => {
        if (volantesStatus.status !== "SUCCESS") return volantesStatus;
        const volantes = volantesStatus.data.volantes.map((invoice) => {
          const checked = addedItems.some((x) => x.id === invoice.id);
          return { checked, invoice };
        });
        return {
          ...volantesStatus,
          data: { ...volantesStatus.data, volantes },
        };
      }
    );

    const checkedAll$ = checkedInvoices$.pipe(
      filter((rowsStatus) => rowsStatus.status === "SUCCESS"),
      map((rowsStatus) => rowsStatus.data.volantes),
      map((rows: any) => {
        return rows.filter((row) => !row.disabledCheckbox);
      }),
      map((rows) => {
        return rows.every((row) => row.checked);
      }),
      tap((checkedAll) => {
        setCheckedAll(checkedAll);
      })
    );

    const checkedAllSubscription = checkedAll$.subscribe();

    const checkedInvoicesSubscription = checkedInvoices$
      .pipe(
        tap((invoicesStatus) => {
          setInvoicesStatus({
            status: invoicesStatus.status,
            data: invoicesStatus.data,
            error: invoicesStatus.error,
            params: invoicesStatus.params,
          });
        })
      )
      .subscribe();

    return () => {
      checkedInvoicesSubscription.unsubscribe();
      checkedAllSubscription.unsubscribe();
    };
  }, [loadInvoices.state$, shoppingCar]);

  const [checkedAll, setCheckedAll] = useState(false);
  const GRID_TEMPLATE_COLUMNS = "4.5rem 35rem repeat(5, 1fr) 10rem";
  const history = useHistory();
  const [showConfirmationModal, updateShowConfirmationModal] = useState(false);

  const [showMessageErrorAppointment, setShowMessageErrorAppointment] =
    useState("");
  const [showModalErrorAppointment, setShowModalErrorAppointment] =
    useState(false);

  const [stepGoNextPage, setStepGoNextPage] = useState(0);

  return (
    <>
      {showModalErrorAppointment && showMessageErrorAppointment && (
        <ConfirmationModal
          title={"Atención"}
          message={showMessageErrorAppointment}
          toggleModal={() => {
            setShowMessageErrorAppointment("");
            setShowModalErrorAppointment(false);
          }}
          onAccept={() => {
            setShowMessageErrorAppointment("");
            setShowModalErrorAppointment(false);
          }}
        />
      )}
      <Authorization actions={["volante.get_volante_appointment"]}>
        {invoicesStatus.status === "SUCCESS" &&
          invoicesStatus.data.volantes.length === 0 && (
            <StandaloneMessage>
              No se encontraron volantes disponibles para generar cita.
            </StandaloneMessage>
          )}
        <VctVolantesContainer gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
          {invoicesStatus.status === "PROCESSING" && (
            <>
              <PaymentLoader numItems={ITEMS_PER_PAGE} />
            </>
          )}
          {invoicesStatus.status === "SUCCESS" &&
            invoicesStatus.data.volantes.length > 0 && (
              <TableHeader className="item-container">
                <Authorization actions={["volante.get_volante_appointment"]}>
                  <DivCheckbox>
                    <Checkbox
                      className="checkbox-volante checkInvoice clickable-element"
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          invoicesStatus.data.volantes
                            .filter((item) => !item.invoice.disabledCheckbox)
                            .forEach((item) => {
                              const { invoice } = item;
                              shoppingCar.addItem(invoice);
                            });
                        } else {
                          invoicesStatus.data.volantes
                            .filter((item) => !item.invoice.disabledCheckbox)
                            .forEach((item) => {
                              const { invoice } = item;
                              shoppingCar.removeItem(invoice.id);
                            });
                        }
                      }}
                      checked={!invoicesStatus.data?.disabledAll && checkedAll}
                      disabled={invoicesStatus.data?.disabledAll}
                    />
                  </DivCheckbox>

                  <div
                    className="importer-header"
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginLeft: "1rem",
                    }}
                  >
                    {TITLES.importer}
                  </div>
                  <div>{TITLES.weight}</div>
                  <div>{TITLES.groups}</div>
                  <div>{TITLES.date}</div>
                  <div>{TITLES.status}</div>
                  <div>{TITLES.observation}</div>
                  <div>{TITLES.actions}</div>
                </Authorization>
              </TableHeader>
            )}
          <div>
            {invoicesStatus.status === "SUCCESS" &&
              invoicesStatus.data.volantes.length > 0 && (
                <div>
                  {invoicesStatus.data.volantes.map((item: Volante) => {
                    const { checked, invoice } = item;
                    const volante = item.invoice;
                    const {
                      withdrawalDate,
                      consigneeName,
                      id,
                      disabledCheckbox,
                      groupsNum,
                    } = volante;
                    return (
                      <VolanteRow
                        key={volante.id}
                        className="item-container"
                        onClick={() => {}}
                      >
                        <div className="attribute">
                          <div className="titles">{TITLES.select}</div>
                          <DivCheckbox>
                            <Checkbox
                              className="checkbox-react-prime-class"
                              checked={checked}
                              onChange={(e) => {
                                if (e.checked) {
                                  shoppingCar.addItem(invoice);
                                } else {
                                  shoppingCar.removeItem(invoice.id);
                                }
                              }}
                              disabled={disabledCheckbox}
                              style={{
                                cursor: `${
                                  disabledCheckbox ? "not-allowed" : "pointer"
                                }`,
                              }}
                            />
                          </DivCheckbox>
                        </div>
                        <div className="attribute">
                          <div className="titles">{TITLES.importer}</div>
                          <div
                            className="double-line-header"
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <p className="importer-id">{id}</p>
                            <p style={{ textAlign: "initial" }}>
                              {consigneeName}
                            </p>
                          </div>
                        </div>
                        <div className="attribute">
                          <div className="titles">{TITLES.weight}</div>
                          <div>{formatNumber(volante.parcelsWeight)} kg</div>
                        </div>
                        <div className="attribute">
                          <div className="titles">{TITLES.groups}</div>
                          <div>{groupsNum}</div>
                        </div>
                        <div className="attribute">
                          <div className="titles">{TITLES.date}</div>
                          <div className="double-line-header">
                            <ColoredLabel isDateExpired={disabledCheckbox}>
                              <p>{`Retiro (${dateToRegularFormat(
                                withdrawalDate
                              )})`}</p>
                            </ColoredLabel>
                          </div>
                        </div>
                        <div className="attribute">
                          <div className="titles">{TITLES.status}</div>
                          <StatusSectionDetails invoice={volante} />
                        </div>
                        <div className="attribute">
                          <div className="titles">{TITLES.observation}</div>
                          <ObservationDiv
                            observation={
                              volante.additionalChargesStatus.category
                            }
                            clickable={
                              volante.additionalChargesStatus.option ===
                              PENDING_PAYMENT
                            }
                          >
                            <span>
                              {volante.additionalChargesStatus.label.toUpperCase()}
                            </span>
                          </ObservationDiv>
                        </div>

                        <TurnCreationTableEditColumn volante={volante} />
                      </VolanteRow>
                    );
                  })}
                </div>
              )}
          </div>
        </VctVolantesContainer>
        {invoicesStatus.status === "SUCCESS" && (
          <>
            {invoicesStatus.data.count > 0 && (
              <Container>
                <div className="flex-paginator" style={{ marginLeft: "2rem" }}>
                  <Paginator
                    first={
                      (invoicesStatus.params.page - 1) *
                      invoicesStatus.data.page_size
                    }
                    rows={invoicesStatus.data.page_size}
                    totalRecords={invoicesStatus.data.count}
                    onPageChange={(e) => {
                      const targetPage =
                        e.first / invoicesStatus.data.page_size;
                      const pathname = history.location.pathname;
                      const parsedParams = queryString.parse(
                        history.location.search
                      );
                      const stringifiedParams = queryString.stringify({
                        ...parsedParams,
                        page: targetPage + 1,
                      });
                      history.push(`${pathname}?${stringifiedParams}`);
                    }}
                  />
                </div>
                <div>
                  <Authorization
                    actions={[
                      "vehicle.get_vehicle_categories",
                      "user.get_customs_dispatchers",
                      "timeslot.get_timeslots",
                    ]}
                  >
                    <TurnCreationScheduleButton
                      shoppingCar={shoppingCar}
                      loadInvoices={loadInvoices}
                      updateShowConfirmationModal={updateShowConfirmationModal}
                      onErrorAppoiment={(error) => {
                        const errorMessage = getErrorValueResponse(error);
                        setShowMessageErrorAppointment(errorMessage);
                        setShowModalErrorAppointment(true);
                      }}
                      countPageRedirect={(data) => {
                        setStepGoNextPage(data);
                      }}
                      dateUrl={dateUrl}
                    />
                  </Authorization>
                </div>
              </Container>
            )}
          </>
        )}

        {showConfirmationModal && (
          <ConfirmationModal
            title={"Cita generada con éxito"}
            message={
              "La cita ha sido creada correctamente. Verifique su cita en la pestaña de seguimiento de citas."
            }
            toggleModal={() => {
              updateShowConfirmationModal(false);
              shoppingCar.items.clear();
              const parsedParams = {
                ...queryString.parse(window.location.search),
                page: 1,
              };
              const stringifiedParams = queryString.stringify(parsedParams);
              const pathname = history.location.pathname;
              if (stepGoNextPage > 0) {
                history.push(`${pathname}?${stringifiedParams}`);
              } else {
                history.push(USER_ROUTES.DATING_TRACKING.path);
              }
            }}
            onAccept={() => {
              updateShowConfirmationModal(false);
              shoppingCar.items.clear();
              const parsedParams = {
                ...queryString.parse(window.location.search),
                page: 1,
              };
              const stringifiedParams = queryString.stringify(parsedParams);
              const pathname = history.location.pathname;
              if (stepGoNextPage > 0) {
                history.push(`${pathname}?${stringifiedParams}`);
              } else {
                history.push(USER_ROUTES.DATING_TRACKING.path);
              }
            }}
          />
        )}
      </Authorization>
    </>
  );
};

const TurnCreationTableEditColumn = (props) => {
  const { volante, actions } = props;
  const { hasStatusToDisable } = volante;

  const [showEditModal, updateEditShowModal] = useState(false);

  return (
    <div className="attribute">
      <div className="titles">{actions}</div>
      <EditButton
        tooltipText="Editar volante"
        onClick={(e) => {
          e.stopPropagation();
          updateEditShowModal(true);
        }}
        disabled={hasStatusToDisable}
      />
      {showEditModal ? (
        <VctVolanteEdition
          volante={volante}
          hideModal={() => {
            updateEditShowModal(false);
          }}
          showModalEdit={() => {
            updateEditShowModal(false);
          }}
        />
      ) : null}
    </div>
  );
};

const TurnCreationScheduleButton = (props) => {
  const {
    shoppingCar,
    loadInvoices,
    updateShowConfirmationModal,
    onErrorAppoiment,
    countPageRedirect,
    dateUrl,
  } = props;

  const [showDiffDaysModal, updateShowDiffDaysModal] = useState(false);

  const [summaryVctVolantes, updateSummaryVctVolantes] = useState({});

  const selectedItems = shoppingCar.items.value;

  const [showAppRequestModal, updateShowAppRequestModal] = useState(false);

  const [firstArrived, setFirstArrived] = useState("");

  const [showMessageDispacherTimeslot, setShowMessageDispacherTimeslot] =
    useState("");

  const [showTitleDispacherTimeslot, setShowTitleDispacherTimeslot] =
    useState("");
  const [showModalLength, setShowModalLength] = useState(false);

  const loadScheduleAppointment = useAsyncExecutor2(
    (params: any) => {
      const { groups_num_gte, withdrawal_date } = params;

      const dispatchers = networkBackendRequest(
        "/customs-dispatchers/",
        HTTP_METHODS.GET
      )
        .then((response) => response.json())
        .then(convertDispatcherResponse)
        .then((resp) => {
          setFirstArrived((firstArrived) => firstArrived || "dispatchers");

          return resp;
        });
      const vehicles = networkBackendRequest(
        `/vehicle-category-options/`,
        HTTP_METHODS.GET
      )
        .then((response) => response.json())
        .then(convertVehicleResponse);

      const timeslots = networkBackendRequest("/timeslots/", HTTP_METHODS.GET, {
        groups_num_gte,
        withdrawal_date,
      })
        .then((response) => response.json())

        .then(convertTimeslotResponse)
        .then((resp) => {
          setFirstArrived((firstArrived) => firstArrived || "timeslots");
          return resp;
        });
      return zip(
        dispatchers,
        vehicles,
        timeslots,
        (dispatchers, vehicles, timeslots) => ({
          dispatchers,
          vehicles,
          timeslots,
        })
      ).pipe(
        map((data) => {
          const [withdrawalDayTxt] = getWithdrawalDayInfo(withdrawal_date);
          return {
            ...data,
            withdrawalDate: withdrawal_date,
            withdrawalDayTxt: withdrawalDayTxt,
          };
        })
      );
    },
    {
      onSuccess: (data) => {
        const { dispatchers, timeslots } = data;
        const enableDisableModalVehicles =
          dispatchers.length > 1 && timeslots.length > 1;
        if (enableDisableModalVehicles) {
          updateShowAppRequestModal(true);
        }
      },
    }
  );

  useEffect(() => {
    if (!loadScheduleAppointment.isSuccess) return;
    if (
      loadScheduleAppointment.data?.dispatchers.length <= 1 &&
      (firstArrived === "dispatchers" || firstArrived === "timeslots")
    ) {
      setShowMessageDispacherTimeslot(
        "Los despachadores aduaneros para la programación de su cita aun no se encuentran habilitados en el sistema. Agradeceremos contactarse con el equipo de Soporte TalmaClick a los siguientes canales:"
      );
      setShowTitleDispacherTimeslot(
        "No existen despachadores aduaneros habilitados"
      );
      setShowModalLength(true);
    }
  }, [
    firstArrived,
    loadScheduleAppointment.isSuccess,
    loadScheduleAppointment.data?.dispatchers,
  ]);

  useEffect(() => {
    if (!loadScheduleAppointment.isSuccess) return;
    if (
      loadScheduleAppointment.data?.timeslots.length <= 1 &&
      (firstArrived === "timeslots" || firstArrived === "dispatchers")
    ) {
      setShowMessageDispacherTimeslot(
        "Los Horarios de turnos para la programación de su cita aun no se encuentran habilitados en el sistema. Agradeceremos contactarse con el equipo de Soporte TalmaClick a los siguientes canales:"
      );
      setShowTitleDispacherTimeslot(
        "No existen horarios de retiro disponibles"
      );
      setShowModalLength(true);
    }
  }, [
    firstArrived,
    loadScheduleAppointment.isSuccess,
    loadScheduleAppointment.data?.timeslots,
  ]);

  const [showErrorMessageLength, setShowErrorMessageLength] = useState("");
  const getAppointmentVolantes: any = useAsyncExecutor2(
    (params: any) => {
      const { shoppingCar } = params;
      const volante_nums = shoppingCar.map((volante) => volante.id);
      return networkBackendRequest(`/volantes/appointment/`, HTTP_METHODS.GET, {
        volante_nums,
        page_size: volante_nums.length,
      })
        .then((resp) => resp.json())
        .then(convertAppointmentResponse);
    },
    {
      onSuccess: (data, params) => {
        const { volantes } = data;
        const { shoppingCar } = params;
        if (shoppingCar.length !== volantes.length) {
          setShowErrorMessageLength(
            "Alguno de los volantes seleccionados ya fueron asociados a una cita, por favor volver intentarlo. Agradeceremos contactarse con el equipo de Soporte Talmaclick."
          );
          updateShowAppRequestModal(false);
        }
      },
      onError: (error) => {
        const errorMessage = getErrorValueResponse(error);

        setShowErrorMessageLength(errorMessage);
      },
    }
  );

  return (
    <ButtonContainer>
      {showDiffDaysModal && (
        <VctAppointmentDiffDaysModal
          hideModal={() => updateShowDiffDaysModal(false)}
        />
      )}
      {showErrorMessageLength && (
        <ConfirmationModalThird
          onAccept={() => {
            setShowErrorMessageLength("");
            shoppingCar.items.clear();
          }}
          title={"Atención"}
          toggleModal={() => {
            setShowErrorMessageLength("");
            shoppingCar.items.clear();
          }}
          buttonTitle={"Entendido"}
          backgroundColor={"#002E6C"}
        >
          <p>
            {showErrorMessageLength}
            Email <b> talmaclick@talma.com.pe</b>
          </p>
          <p>
            Cel: <b>981069112 / 936518110</b>{" "}
          </p>
        </ConfirmationModalThird>
      )}

      {showMessageDispacherTimeslot &&
        showTitleDispacherTimeslot &&
        showModalLength && (
          <ConfirmationModalThird
            onAccept={() => {
              setShowModalLength(false);
              setShowMessageDispacherTimeslot("");
              setShowTitleDispacherTimeslot("");
              setFirstArrived("");
            }}
            title={showTitleDispacherTimeslot}
            toggleModal={() => {
              setShowModalLength(false);
              setShowMessageDispacherTimeslot("");
              setShowTitleDispacherTimeslot("");
              setFirstArrived("");
            }}
            buttonTitle={"Entendido"}
            backgroundColor={"#002E6C"}
          >
            <p>
              {showMessageDispacherTimeslot}
              Email <b> talmaclick@talma.com.pe</b>
            </p>
            <p>
              Cel: <b>981069112 / 936518110</b>{" "}
            </p>
          </ConfirmationModalThird>
        )}
      {showAppRequestModal && (
        <VctAppointmentRequest
          selectedVolantes={selectedItems}
          hideModal={() => {
            updateShowAppRequestModal(false);
          }}
          handleClosingVctAppointmentModal={() => {
            updateShowAppRequestModal(false);
          }}
          summaryVctVolantes={summaryVctVolantes}
          dispatchers={loadScheduleAppointment.data.dispatchers}
          vehicleCategories={loadScheduleAppointment.data.vehicles}
          timeslots={loadScheduleAppointment.data.timeslots}
          withdrawalDayTxt={loadScheduleAppointment.data.withdrawalDayTxt}
          withdrawalDate={loadScheduleAppointment.data.withdrawalDate}
          showModalHide={() => {
            updateShowAppRequestModal(false);
          }}
          modalHideAppointment={() => {
            updateShowAppRequestModal(false);
          }}
          updateShowConfirmationModal={updateShowConfirmationModal}
          onErrorAppoiment={onErrorAppoiment}
          countPageRedirect={countPageRedirect}
          dateUrl={dateUrl}
          shoppingCar={shoppingCar.items.value}
          getAppointmentVolantes={getAppointmentVolantes}
        />
      )}

      <MainButton
        disabled={
          selectedItems.length === 0 ||
          loadInvoices.isLoading ||
          loadScheduleAppointment.isLoading
        }
        onClick={() => {
          if (
            checkEqualWithdrawalDates(selectedItems) === RESULT.DIFFERENT_DAYS
          ) {
            updateShowDiffDaysModal(true);
          } else {
            const summaryVolantes = calculateSummaryVolante(
              shoppingCar.items.value
            );
            updateSummaryVctVolantes(summaryVolantes);
            const firstSelectedItem = selectedItems[0];
            const groups_num_gte = summaryVolantes.groupsTotal;
            loadScheduleAppointment.execute({
              groups_num_gte,
              withdrawal_date: firstSelectedItem.withdrawalDate,
            });
          }
        }}
        isLoading={loadInvoices.isLoading}
      >
        {loadScheduleAppointment.isLoading && (
          <img
            src={loadingGreen}
            className="icon-image"
            alt="Processing request"
          />
        )}
        {"Programar cita (" + shoppingCar.items.value.length + ")"}
      </MainButton>
    </ButtonContainer>
  );
};

const convertDispatcherResponse = (resp) => {
  const data = [
    { label: "-seleccione-", value: "" },
    ...resp.customs_dispatchers.map((detail) => {
      return {
        dispatcherId: detail.dispatcher_id,
        dispatcherName: detail.name,
        label: detail.name,
        value: detail.dispatcher_id,
      };
    }),
  ];
  return data;
};

const convertVehicleResponse = (resp) => {
  const data = {
    maxVehicles: resp.limit_number_vehicles,
    labels: [
      { label: "-seleccione-", value: "" },
      ...resp.vehicle_category_options.map((vehicleCategory) => {
        return {
          label: vehicleCategory.label,
          value: vehicleCategory.option,
        };
      }),
    ],
  };
  return data;
};

const convertTimeslotResponse = (resp) => {
  const details = resp.timeslots.map((detail) => {
    return {
      timeslotId: detail.timeslot_id,
      startDate: detail.start,
      endDate: detail.end,
      label: formatTime(detail.start),
      value: detail.timeslot_id,
    };
  });

  details.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    } else if (a.label === b.label) {
      return 0;
    } else {
      return 1;
    }
  });

  return [{ label: "-seleccione-", value: "" }, ...details];
};

function formatTime(time) {
  const timeDate = new Date(time);
  return format(timeDate, "HH:mm");
}

function checkEqualWithdrawalDates(selectedVolantes) {
  if (selectedVolantes.length === 1) {
    return RESULT.OK;
  }
  let withdrawalDate = "";
  for (let volante of selectedVolantes) {
    if (withdrawalDate === "") {
      withdrawalDate = volante.withdrawalDate;
    }
    if (volante.withdrawalDate !== withdrawalDate) {
      return RESULT.DIFFERENT_DAYS;
    }
  }
  return RESULT.OK;
}

function calculateSummaryVolante(volantes) {
  let volantesQty = volantes.length;
  let parcelsQty = 0.0;
  let parcelsWeight = 0.0;
  let groupsTotal = 0.0;
  let volanteIds = [];

  for (let volante of volantes) {
    parcelsQty += volante.parcelsNum;
    parcelsWeight += volante.parcelsWeight;
    groupsTotal += volante.groupsNum;
    volanteIds.push(volante.id);
  }
  return {
    volantesQty,
    parcelsQty,
    parcelsWeight,
    groupsTotal,
    volanteIds,
  };
}

function getWithdrawalDayInfo(date) {
  let withdrawalDate = moment.tz(date, "America/Lima").startOf("day");
  let withdrawalOption = withdrawalDate.calendar(null, {
    sameDay: "[Hoy]",
    nextDay: "[Mañana]",
    sameElse: "DD/MM/YYYY",
  });
  let withdrawalSelection = "-";
  if (withdrawalOption === "Hoy") withdrawalSelection = "today";
  else if (withdrawalOption === "Mañana") withdrawalSelection = "tomorrow";
  return [withdrawalOption, withdrawalSelection];
}
